/**
 * Inline PDF viewer (pdf.js) — renders EVERY page stacked in one continuous scroll
 * (Phase 7 feedback: "one scroll, no pages"), with working zoom. `goToPage` scrolls the
 * target page into view; the current page (for the URL and the markup overlay) tracks
 * whichever page is most visible in the scroll viewport. Used for the report, the letter
 * of instruction, and the valuer's annotated feedback copy.
 *
 * Zoom renders each canvas at (zoom × BASE_SCALE) and sizes it explicitly in CSS px —
 * the previous max-width:100% cap silently undid any zoom, which is why it "didn't work".
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
// eslint-disable-next-line import/no-unresolved -- Vite ?url asset import
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Banner, Spinner } from './ui';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export interface PdfViewerHandle {
  goToPage: (page: number) => void;
}

interface PdfViewerProps {
  url: string | null;
  onPageChange?: (page: number) => void;
  /**
   * Phase 7: an overlay rendered exactly over each page's canvas (same box, same aspect
   * ratio), for the reviewer markup layer. Receives that page's number and (Phase 8, for
   * the compliance-highlight layer's snippet lookup) the loaded pdf.js document.
   */
  renderOverlay?: (page: number, doc: PDFDocumentProxy) => ReactNode;
  /**
   * The markup tool selector (highlight/note/box/pen/undo), rendered INSIDE this
   * component's own DOM subtree rather than as a sibling in the page - full screen
   * (see the toolbar's fullscreen button) only shows the requested element's own
   * subtree, so anything rendered outside it would otherwise vanish in full screen.
   */
  markupToolbar?: ReactNode;
  /**
   * Rendered to the right of the page stage, but ONLY while in full screen (see
   * markupToolbar's docstring - anything outside this component vanishes in full
   * screen). Normally the caller renders its own equivalent panel elsewhere on the
   * page for the non-fullscreen view; this exists so the reviewer isn't cut off from
   * it (e.g. the markup notes list) while full screen.
   */
  sidePanel?: ReactNode;
}

const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2.4;
const ZOOM_STEP = 0.2;
const BASE_SCALE = 1.35;
/** Cap the backing-store resolution so zooming doesn't re-rasterise huge documents. */
const RENDER_DPR = Math.min(window.devicePixelRatio || 1, 2);

function PageCanvas({
  doc,
  pageNumber,
  zoom,
  renderOverlay,
}: {
  doc: PDFDocumentProxy;
  pageNumber: number;
  zoom: number;
  renderOverlay?: (page: number, doc: PDFDocumentProxy) => ReactNode;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    void doc.getPage(pageNumber).then(async (pdfPage) => {
      if (cancelled || !canvasRef.current) return;
      const cssViewport = pdfPage.getViewport({ scale: zoom * BASE_SCALE });
      const renderViewport = pdfPage.getViewport({ scale: zoom * BASE_SCALE * RENDER_DPR });
      const target = canvasRef.current;
      target.width = renderViewport.width;
      target.height = renderViewport.height;
      target.style.width = `${Math.floor(cssViewport.width)}px`;
      target.style.height = `${Math.floor(cssViewport.height)}px`;
      const ctx = target.getContext('2d');
      if (!ctx) return;
      await pdfPage.render({ canvasContext: ctx, viewport: renderViewport }).promise;
    });
    return () => {
      cancelled = true;
    };
  }, [doc, pageNumber, zoom]);

  return (
    <div className="pdfviewer__page" data-page={pageNumber}>
      <canvas ref={canvasRef} />
      {renderOverlay ? <div className="pdfviewer__overlay">{renderOverlay(pageNumber, doc)}</div> : null}
    </div>
  );
}

export const PdfViewer = forwardRef<PdfViewerHandle, PdfViewerProps>(function PdfViewer(
  { url, onPageChange, renderOverlay, markupToolbar, sidePanel },
  ref,
) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Tracks the real fullscreen state (not just our own toggle) so pressing Esc,
  // or the browser exiting fullscreen for any other reason, stays in sync.
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void rootRef.current?.requestFullscreen();
    }
  }

  // Load the document whenever the (signed) URL changes.
  useEffect(() => {
    let cancelled = false;
    setDoc(null);
    setStatus('loading');
    if (!url) return;
    pdfjsLib
      .getDocument(url)
      .promise.then((loaded) => {
        if (cancelled) {
          void loaded.destroy();
          return;
        }
        setDoc(loaded);
        setPageCount(loaded.numPages);
        setCurrentPage(1);
        setPageInput('1');
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const scrollToPage = useCallback((target: number) => {
    const stage = stageRef.current;
    if (!stage) return;
    const el = stage.querySelector<HTMLElement>(`[data-page="${target}"]`);
    el?.scrollIntoView({ block: 'start' });
  }, []);

  useImperativeHandle(ref, () => ({
    goToPage(target: number) {
      const clamped = Math.min(Math.max(1, Math.round(target)), pageCount || Math.round(target));
      scrollToPage(clamped);
      setCurrentPage(clamped);
      setPageInput(String(clamped));
      onPageChange?.(clamped);
    },
  }));

  // Track which page is most visible so the toolbar/URL/overlay follow the scroll.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || status !== 'ready') return;
    const visibility = new Map<number, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const page = Number((entry.target as HTMLElement).dataset['page']);
          visibility.set(page, entry.intersectionRatio);
        }
        let best = 1;
        let bestRatio = -1;
        for (const [page, ratio] of visibility) {
          if (ratio > bestRatio) {
            best = page;
            bestRatio = ratio;
          }
        }
        setCurrentPage((prev) => {
          if (prev === best) return prev;
          setPageInput(String(best));
          onPageChange?.(best);
          return best;
        });
      },
      { root: stage, threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    for (const el of stage.querySelectorAll('[data-page]')) observer.observe(el);
    return () => observer.disconnect();
  }, [status, pageCount, zoom, onPageChange]);

  function commitPageInput() {
    const n = Number(pageInput);
    if (Number.isFinite(n) && n >= 1) {
      const clamped = pageCount ? Math.min(Math.max(1, n), pageCount) : n;
      scrollToPage(clamped);
      setPageInput(String(clamped));
    } else {
      setPageInput(String(currentPage));
    }
  }

  return (
    <div className={`pdfviewer${isFullscreen ? ' pdfviewer--fullscreen' : ''}`} ref={rootRef}>
      <div className="pdfviewer__toolbar">
        <div className="pdfviewer__nav">
          <input
            className="pdfviewer__page-input"
            value={pageInput}
            disabled={status !== 'ready'}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={commitPageInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            aria-label="Page number"
          />
          <span className="muted">/ {pageCount || '—'}</span>
        </div>
        <div className="pdfviewer__zoom">
          <button
            type="button"
            className="pdfviewer__btn"
            aria-label="Zoom out"
            disabled={status !== 'ready' || zoom <= MIN_ZOOM}
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, Number((z - ZOOM_STEP).toFixed(2))))}
          >
            −
          </button>
          <span className="muted">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            className="pdfviewer__btn"
            aria-label="Zoom in"
            disabled={status !== 'ready' || zoom >= MAX_ZOOM}
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, Number((z + ZOOM_STEP).toFixed(2))))}
          >
            +
          </button>
          <button
            type="button"
            className="pdfviewer__btn"
            aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
            title={isFullscreen ? 'Exit full screen' : 'Full screen'}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? '⤡' : '⤢'}
          </button>
        </div>
      </div>

      {markupToolbar ? <div className="pdfviewer__markup-toolbar">{markupToolbar}</div> : null}

      <div className="pdfviewer__body">
        <div className="pdfviewer__stage" ref={stageRef}>
          {status === 'loading' ? (
            <div className="pdfviewer__center">
              <Spinner label="Loading document…" />
            </div>
          ) : null}
          {status === 'error' ? (
            <div className="pdfviewer__center">
              <Banner tone="error">This document couldn’t be loaded.</Banner>
            </div>
          ) : null}
          {status === 'ready' && doc
            ? Array.from({ length: pageCount }, (_, i) => (
                <PageCanvas key={i + 1} doc={doc} pageNumber={i + 1} zoom={zoom} renderOverlay={renderOverlay} />
              ))
            : null}
        </div>

        {/* Only shown in full screen - the caller renders its own equivalent panel elsewhere on the page for the normal view. */}
        {isFullscreen && sidePanel ? <div className="pdfviewer__sidepanel stack">{sidePanel}</div> : null}
      </div>
    </div>
  );
});
